import React, { useState } from "react";
import JSZip from "jszip";
import { ExportManager, type ExportFormat } from "@/lib/cat/exportManager";
import {
    Download,
    FileText,
    FileSpreadsheet,
    FileCode,
    X,
    CheckCircle,
} from "lucide-react";
import Button from "@/components/common/Button";
import { toPreAO } from "@/lib/cat/orthoConverter";

interface ExportDialogProps {
    isOpen: boolean;
    onClose: () => void;
    project: {
        id: string;
        name: string;
        source_language: string;
        target_language: string;
        total_segments: number;
        completed_segments: number;
        word_count: number;
        orthographic_standard?: string;
        file_url?: string | null;
    };
    segments: Array<{
        id: string;
        source_text: string;
        target_text: string;
        segment_order: number;
        status:
            | "new"
            | "in_progress"
            | "completed"
            | "approved"
            | "confirmed"
            | "rejected";
        locked: boolean;
        notes: string;
        source_file?: string;
    }>;
    availableFiles?: string[];
    initialFileScope?: string;
    fileUrls?: Record<string, string>;
}

const ExportDialog: React.FC<ExportDialogProps> = ({
    isOpen,
    onClose,
    project,
    segments,
    availableFiles,
    initialFileScope,
    fileUrls,
}) => {
    const [selectedFormat, setSelectedFormat] = useState<ExportFormat>("xliff");
    const [exporting, setExporting] = useState(false);
    const [exportSuccess, setExportSuccess] = useState(false);
    const [exportFileScope, setExportFileScope] = useState<string>(
        initialFileScope && initialFileScope !== "all"
            ? initialFileScope
            : "all",
    );

    const formats: Array<{
        id: ExportFormat;
        name: string;
        description: string;
        icon: React.ReactNode;
        extension: string;
    }> = [
        {
            id: "xliff",
            name: "XLIFF",
            description: "Standard translation exchange format",
            icon: <FileCode className="h-5 w-5" />,
            extension: ".xlf",
        },
        {
            id: "docx-bilingual",
            name: "Word (Bilingual)",
            description: "Source and target in table format",
            icon: <FileText className="h-5 w-5" />,
            extension: ".docx",
        },
        {
            id: "docx-target",
            name: "Word (Target Only)",
            description: "Final translation document",
            icon: <FileText className="h-5 w-5" />,
            extension: ".docx",
        },
        {
            id: "pdf-bilingual",
            name: "PDF (Bilingual)",
            description: "Source and target for review",
            icon: <FileText className="h-5 w-5" />,
            extension: ".pdf",
        },
        {
            id: "pdf-target",
            name: "PDF (Target Only)",
            description: "Final translation PDF",
            icon: <FileText className="h-5 w-5" />,
            extension: ".pdf",
        },
        {
            id: "txt",
            name: "Plain Text",
            description: "Target text only",
            icon: <FileText className="h-5 w-5" />,
            extension: ".txt",
        },
        {
            id: "tmx",
            name: "TMX (Translation Memory)",
            description: "Export completed segments to TM",
            icon: <FileSpreadsheet className="h-5 w-5" />,
            extension: ".tmx",
        },
    ];

    const handleExport = async () => {
        setExporting(true);
        setExportSuccess(false);

        try {
            console.log(`📤 Exporting as ${selectedFormat}...`);

            // Filter segments by file scope
            const filteredSegments =
                exportFileScope === "all"
                    ? segments
                    : segments.filter((s) => s.source_file === exportFileScope);

            // Apply Pre-AO conversion if project uses Pre-AO standard
            const segmentsToExport =
                project.orthographic_standard === "pre_ao"
                    ? filteredSegments.map((s) => ({
                          ...s,
                          target_text: s.target_text
                              ? toPreAO(s.target_text).text
                              : s.target_text,
                      }))
                    : filteredSegments;

            // Force bilingual if formatted export unavailable for this file
            let activeFormat = selectedFormat;
            if (
                exportFileScope !== "all" &&
                fileUrls &&
                !fileUrls[exportFileScope] &&
                activeFormat === "docx-target"
            ) {
                activeFormat = "docx-bilingual";
            }

            // Multi-file DOCX export: zip per-file DOCX when exporting all files
            const isMultiFileDocx =
                activeFormat &&
                (activeFormat === "docx-target" ||
                    activeFormat === "docx-bilingual") &&
                exportFileScope === "all" &&
                availableFiles &&
                availableFiles.length > 1;

            if (isMultiFileDocx) {
                const exportedBlobs: { filename: string; blob: Blob }[] = [];
                for (const fileName of availableFiles) {
                    const fileSegments = segmentsToExport.filter(
                        (s) => s.source_file === fileName,
                    );
                    if (fileSegments.length === 0) continue;
                    const fileUrl = fileUrls?.[fileName] ?? project.file_url;
                    const projWithUrl = fileUrl
                        ? { ...project, file_url: fileUrl }
                        : project;
                    const { blob } = await ExportManager.exportProject(
                        projWithUrl,
                        fileSegments,
                        activeFormat,
                    );
                    const baseName = fileName.replace(/\.[^/.]+$/, "");
                    exportedBlobs.push({
                        filename: `${project.name} — ${baseName}.docx`,
                        blob,
                    });
                }
                const zip = new JSZip();
                for (const { filename, blob } of exportedBlobs) {
                    zip.file(filename, blob);
                }
                const zipBlob = await zip.generateAsync({ type: "blob" });
                const url = URL.createObjectURL(zipBlob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `${project.name} — All Files.zip`;
                a.click();
                URL.revokeObjectURL(url);
            } else {
                // Resolve DOCX source URL for XML surgery
                const docxSourceUrl =
                    exportFileScope !== "all" &&
                    fileUrls &&
                    fileUrls[exportFileScope]
                        ? fileUrls[exportFileScope]
                        : project.file_url;

                const exportBaseName =
                    exportFileScope === "all"
                        ? project.name
                        : `${project.name} — ${exportFileScope.replace(/\.[^/.]+$/, "")}`;

                const updatedProject = docxSourceUrl
                    ? { ...project, file_url: docxSourceUrl }
                    : project;

                const { blob, filename } = await ExportManager.exportProject(
                    updatedProject,
                    segmentsToExport,
                    activeFormat,
                );

                // Override filename with scoped name
                const sanitizeName = (n: string) =>
                    n.replace(/[^a-zA-Z0-9_-]/g, "_");
                const scopedFilename =
                    exportFileScope === "all"
                        ? filename
                        : `${sanitizeName(exportBaseName)}${filename.includes(".") ? filename.slice(filename.lastIndexOf(".")) : ""}`;

                ExportManager.downloadBlob(blob, scopedFilename);
            }

            setExportSuccess(true);

            // Auto-close after 2 seconds
            setTimeout(() => {
                onClose();
                setExportSuccess(false);
            }, 2000);
        } catch (error) {
            console.error("Export failed:", error);
            alert("Export failed. Please try again.");
        } finally {
            setExporting(false);
        }
    };

    const selectedFormatInfo = formats.find((f) => f.id === selectedFormat);
    const completedCount = segments.filter(
        (s) => s.status === "confirmed",
    ).length;

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900">
                            Export Project
                        </h2>
                        <p className="text-sm text-gray-500 mt-1">
                            {project.name}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="h-5 w-5 text-gray-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {/* Project Info */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                        <div className="grid grid-cols-3 gap-4 text-sm">
                            <div>
                                <span className="text-blue-600 font-medium">
                                    Languages
                                </span>
                                <p className="text-blue-900 mt-1">
                                    {project.source_language.toUpperCase()} →{" "}
                                    {project.target_language.toUpperCase()}
                                </p>
                            </div>
                            <div>
                                <span className="text-blue-600 font-medium">
                                    Segments
                                </span>
                                <p className="text-blue-900 mt-1">
                                    {project.total_segments} total
                                </p>
                            </div>
                            <div>
                                <span className="text-blue-600 font-medium">
                                    Completed
                                </span>
                                <p className="text-blue-900 mt-1">
                                    {completedCount} / {project.total_segments}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Format Selection */}
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">
                            Select Export Format
                        </h3>
                        <div className="grid grid-cols-1 gap-3">
                            {formats.map((format) => (
                                <button
                                    key={format.id}
                                    onClick={() => setSelectedFormat(format.id)}
                                    className={`flex items-start p-4 border-2 rounded-lg transition-all ${
                                        selectedFormat === format.id
                                            ? "border-blue-500 bg-blue-50"
                                            : "border-gray-200 hover:border-gray-300 bg-white"
                                    }`}
                                >
                                    <div
                                        className={`p-2 rounded-lg mr-3 ${
                                            selectedFormat === format.id
                                                ? "bg-blue-100 text-blue-600"
                                                : "bg-gray-100 text-gray-600"
                                        }`}
                                    >
                                        {format.icon}
                                    </div>
                                    <div className="flex-1 text-left">
                                        <div className="flex items-center space-x-2">
                                            <h4 className="font-semibold text-gray-900">
                                                {format.name}
                                            </h4>
                                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                                                {format.extension}
                                            </span>
                                        </div>
                                        <p className="text-sm text-gray-600 mt-1">
                                            {format.description}
                                        </p>
                                    </div>
                                    {selectedFormat === format.id && (
                                        <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 ml-2" />
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* File Scope Selection */}
                    {availableFiles && availableFiles.length > 0 && (
                        <div className="mt-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">
                                File Scope
                            </h3>
                            <div className="flex flex-wrap items-center gap-2">
                                <button
                                    onClick={() => setExportFileScope("all")}
                                    className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-all ${
                                        exportFileScope === "all"
                                            ? "bg-blue-100 border-blue-500 text-blue-700"
                                            : "bg-white border-gray-300 text-gray-700 hover:border-gray-400"
                                    }`}
                                >
                                    All files
                                </button>
                                {initialFileScope &&
                                    initialFileScope !== "all" && (
                                        <button
                                            onClick={() =>
                                                setExportFileScope(
                                                    initialFileScope,
                                                )
                                            }
                                            className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-all ${
                                                exportFileScope ===
                                                initialFileScope
                                                    ? "bg-blue-100 border-blue-500 text-blue-700"
                                                    : "bg-white border-gray-300 text-gray-700 hover:border-gray-400"
                                            }`}
                                        >
                                            Current file
                                        </button>
                                    )}
                                <button
                                    onClick={() =>
                                        setExportFileScope(
                                            exportFileScope === "all" ||
                                                exportFileScope ===
                                                    initialFileScope
                                                ? availableFiles[0]
                                                : "all",
                                        )
                                    }
                                    className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-all ${
                                        exportFileScope !== "all" &&
                                        exportFileScope !== initialFileScope
                                            ? "bg-blue-100 border-blue-500 text-blue-700"
                                            : "bg-white border-gray-300 text-gray-700 hover:border-gray-400"
                                    }`}
                                >
                                    Choose file
                                </button>
                                {exportFileScope !== "all" &&
                                    exportFileScope !== initialFileScope && (
                                        <select
                                            value={exportFileScope}
                                            onChange={(e) =>
                                                setExportFileScope(
                                                    e.target.value,
                                                )
                                            }
                                            className="px-2 py-1.5 text-sm rounded-lg border border-gray-300 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                        >
                                            {availableFiles.map((f) => (
                                                <option key={f} value={f}>
                                                    {f}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                            </div>
                            {/* Amber warning for missing file URL */}
                            {exportFileScope !== "all" &&
                                fileUrls &&
                                !fileUrls[exportFileScope] && (
                                    <div className="mt-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                                        ⚠ Formatted export unavailable for this
                                        file — original document not found. Will
                                        export as bilingual Word.
                                    </div>
                                )}
                        </div>
                    )}

                    {/* TMX Warning */}
                    {selectedFormat === "tmx" && completedCount === 0 && (
                        <div className="mt-4 bg-gold-50 border border-gold-200 rounded-lg p-4">
                            <div className="flex items-start space-x-3">
                                <span className="text-2xl">⚠️</span>
                                <div>
                                    <h4 className="font-medium text-gold-900 mb-1">
                                        No Completed Segments
                                    </h4>
                                    <p className="text-sm text-gold-800">
                                        TMX export only includes completed or
                                        approved segments. You currently have{" "}
                                        {completedCount} completed segments.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Pre-AO Warning */}
                    {project.orthographic_standard === "pre_ao" && (
                        <div
                            className="flex items-center gap-2 px-3 py-2 bg-amber-50
              border border-amber-200 rounded-lg text-sm text-amber-800 mb-3"
                        >
                            <span>⚠</span>
                            <span>
                                Pre-AO conversion will be applied to all target
                                segments on export.
                            </span>
                        </div>
                    )}

                    {/* Format Info */}
                    {selectedFormatInfo && (
                        <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
                            <h4 className="font-medium text-gray-900 mb-2">
                                Export Details
                            </h4>
                            <div className="space-y-2 text-sm text-gray-700">
                                <div className="flex justify-between">
                                    <span>Format:</span>
                                    <span className="font-medium">
                                        {selectedFormatInfo.name}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span>File extension:</span>
                                    <span className="font-medium">
                                        {selectedFormatInfo.extension}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Segments to export:</span>
                                    <span className="font-medium">
                                        {selectedFormat === "tmx"
                                            ? completedCount
                                            : project.total_segments}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                    <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-600">
                            {exportSuccess ? (
                                <span className="text-green-600 font-medium flex items-center">
                                    <CheckCircle className="h-4 w-4 mr-2" />
                                    Export successful!
                                </span>
                            ) : (
                                <span>
                                    Selected: {selectedFormatInfo?.name}
                                </span>
                            )}
                        </div>
                        <div className="flex items-center space-x-3">
                            <Button
                                variant="secondary"
                                onClick={onClose}
                                disabled={exporting}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                leftIcon={
                                    exporting ? undefined : (
                                        <Download className="h-4 w-4" />
                                    )
                                }
                                onClick={handleExport}
                                disabled={
                                    exporting ||
                                    (selectedFormat === "tmx" &&
                                        completedCount === 0)
                                }
                            >
                                {exporting ? (
                                    <span className="flex items-center">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        Exporting...
                                    </span>
                                ) : (
                                    "Export"
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExportDialog;
